import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import ContactPreferences from "./ContactPreferences";
import RequiredDocuments from "./RequiredDocuments";
import Acknowledgements from "./Acknowledgements";

const PrescriptionOpportunity = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
  customerId,
}) => {
  const [validation, setValidation] = useState({
    documents: false, // Required documents need to be checked
    acknowledgements: false, // Attestation is required
  });

  const setDocumentsValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.documents === isValid) return prev;
      return { ...prev, documents: isValid };
    });
  }, []);

  const setAcknowledgementsValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.acknowledgements === isValid) return prev;
      return { ...prev, acknowledgements: isValid };
    });
  }, []);

  useEffect(() => {
    if (onValidationChange) {
      const allValid = Object.values(validation).every((v) => v === true);
      onValidationChange(allValid);
    }
  }, [validation, onValidationChange]);
  return (
    <Box sx={{ display: "flex", gap: 3, width: "100%", alignItems: "stretch" }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <ContactPreferences
          data={{
            ...data.preferences,
            daysSupply:
              data.preferences?.daysSupply ?? opportunity?.daySupply ?? "",
            quantity:
              data.preferences?.quantity ?? opportunity?.metricQty ?? "",
          }}
          onDataChange={useCallback(
            (newData) => onDataChange("preferences", newData),
            [onDataChange],
          )}
          showErrors={showErrors}
        />
        <RequiredDocuments
          data={data.documents}
          onDataChange={useCallback(
            (newData) => onDataChange("documents", newData),
            [onDataChange],
          )}
          onValidationChange={setDocumentsValid}
          onFilesChange={useCallback(
            (files) => onDataChange("uploadedFiles", files),
            [onDataChange],
          )}
          customerId={customerId}
          showErrors={showErrors}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Acknowledgements
          data={data.acknowledgements}
          onDataChange={useCallback(
            (newData) => onDataChange("acknowledgements", newData),
            [onDataChange],
          )}
          onValidationChange={setAcknowledgementsValid}
          showErrors={showErrors}
          consentHeader={opportunity?.vendor?.memberConsentHeader}
          consentDetail={opportunity?.vendor?.memberConsentDetail}
        />
      </Box>
    </Box>
  );
};

PrescriptionOpportunity.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PrescriptionOpportunity;
